// src/services/autoPilotService.js
import { api } from './apiService';

/**
 * Start a new autopilot session and generate an ICP based on user intent
 * @param {Object} data - Session start data
 * @param {string} data.intent - User's search intent/description (required)
 * @param {number} [data.targetProspects] - Target number of prospects (optional, default: 20)
 * @returns {Promise<Object>} Response with sessionId, status, and icpId
 * @example
 * const response = await startSession({ intent: "Find software engineers in San Francisco", targetProspects: 20 });
 */
export async function startSession({ intent, targetProspects = 20 }) {
  try {
    const response = await api.post('/autopilot/start', {
      intent,
      targetProspects
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Get paginated list of user's autopilot sessions
 * @param {Object} [query] - Query parameters
 * @param {number} [query.page=1] - Page number for pagination
 * @param {number} [query.limit=10] - Number of sessions per page
 * @returns {Promise<Object>} Response with sessions array and pagination metadata
 * @example
 * const response = await getSessions({ page: 1, limit: 10 });
 */
export async function getSessions({ page = 1, limit = 10 } = {}) {
  try {
    const response = await api.get('/autopilot/sessions', {
      params: { page, limit }
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Get the current status and details of a specific autopilot session
 * @param {string} sessionId - The ID of the autopilot session
 * @returns {Promise<Object>} Response with session details including status, progress, and ICP
 * @example
 * const session = await getSessionStatus('507f1f77bcf86cd799439011');
 */
export async function getSessionStatus(sessionId) {
  try {
    const response = await api.get(`/autopilot/${sessionId}/status`);
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Validate or reject the generated ICP for a session
 * @param {string} sessionId - The ID of the autopilot session
 * @param {Object} data - Validation data
 * @param {boolean} data.validated - Whether the ICP is validated
 * @param {string} [data.feedback] - Optional user feedback about the ICP
 * @returns {Promise<Object>} Response with validation result
 * @example
 * const response = await validateICP('507f1f77bcf86cd799439011', { validated: true, feedback: 'Looks good!' });
 */
export async function validateICP(sessionId, { validated, feedback }) {
  try {
    const response = await api.post(`/autopilot/${sessionId}/validate-icp`, {
      validated,
      feedback
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Get the generated ICP (Ideal Customer Profile) for a specific session
 * @param {string} sessionId - The ID of the autopilot session
 * @returns {Promise<Object>} Response with ICP details including attributes, structure, and search combinations
 * @example
 * const icp = await getICP('507f1f77bcf86cd799439011');
 */
export async function getICP(sessionId) {
  try {
    const response = await api.get(`/autopilot/${sessionId}/icp`);
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Manually trigger the autopilot processing for a session
 * @param {string} sessionId - The ID of the autopilot session
 * @returns {Promise<Object>} Response indicating processing has started
 * @example
 * const response = await processSession('507f1f77bcf86cd799439011');
 */
export async function processSession(sessionId) {
  try {
    const response = await api.post(`/autopilot/${sessionId}/process`);
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Generate ICP (Ideal Customer Profile) directly from user intent
 * This is a standalone endpoint that generates ICP without starting an autopilot session
 * @param {string} intent - User's search intent/description (required)
 * @returns {Promise<Object>} Response with generated ICP data
 * @example
 * const response = await generateICPStandalone('find VP of sales currently working for Walmart in Bay Area for Camera category');
 */
export async function generateICPStandalone(intent) {
  try {
    const response = await api.post('/icp/generate', {
      intent
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

// Legacy functions - kept for backward compatibility but deprecated
// These will be removed in future versions

/**
 * @deprecated Use startSession instead. This function is kept for backward compatibility.
 * Generate ICP (Ideal Customer Profile) from user intent
 * @param {string} intent - Natural language intent from user
 * @returns {Promise} Response with structured ICP data
 */
export async function generateICP(intent) {
  console.warn('generateICP is deprecated. Use startSession instead.');
  try {
    const response = await startSession({ intent });
    // If the response has ICP data, return it in the old format
    if (response.status === 'success' && response.data) {
      return {
        status: 'success',
        data: response.data
      };
    }
    return response;
  } catch (err) {
    throw err;
  }
}

/**
 * @deprecated Use validateICP instead. This function is kept for backward compatibility.
 * Validate and update ICP with user feedback
 * @param {Object} icpData - Generated ICP data
 * @param {Object} userFeedback - User modifications/feedback
 * @returns {Promise} Response with validated ICP
 */
export async function validateICP_legacy(icpData, userFeedback) {
  console.warn('validateICP_legacy is deprecated. Use validateICP with sessionId instead.');
  // This is a placeholder - the new API requires sessionId
  throw new Error('Legacy validateICP is deprecated. Please use the new session-based API.');
}

/**
 * @deprecated This endpoint is no longer available. Search queries are generated automatically.
 * Generate 5-8 search queries based on validated ICP
 * @param {Object} icpData - Validated ICP data
 * @returns {Promise} Response with array of search queries
 */
export async function generateSearchQueries(icpData) {
  console.warn('generateSearchQueries is deprecated. Search queries are now generated automatically.');
  throw new Error('This endpoint is no longer available. Search queries are generated automatically.');
}

/**
 * @deprecated This endpoint is no longer available. Searches are executed automatically.
 * Execute multiple searches concurrently
 * @param {Array} searchQueries - Array of search query objects
 * @returns {Promise} Response with combined search results
 */
export async function executeConcurrentSearches(searchQueries) {
  console.warn('executeConcurrentSearches is deprecated. Searches are now executed automatically.');
  throw new Error('This endpoint is no longer available. Searches are executed automatically.');
}

/**
 * @deprecated This endpoint is no longer available. Filtering happens automatically.
 * Filter search results based on ICP metadata matching
 * @param {Array} searchResults - Raw search results
 * @param {Object} icpData - ICP data for filtering criteria
 * @returns {Promise} Response with filtered results
 */
export async function filterResults(searchResults, icpData) {
  console.warn('filterResults is deprecated. Filtering now happens automatically.');
  throw new Error('This endpoint is no longer available. Filtering happens automatically.');
}

/**
 * @deprecated This endpoint is no longer available. Enrichment happens automatically.
 * Enrich prospects via PDL (People Data Labs) API
 * @param {Array} prospectIds - Array of prospect IDs to enrich
 * @returns {Promise} Response with enriched prospect data
 */
export async function enrichProspects(prospectIds) {
  console.warn('enrichProspects is deprecated. Enrichment now happens automatically.');
  throw new Error('This endpoint is no longer available. Enrichment happens automatically.');
}

/**
 * @deprecated This endpoint is no longer available. Verification happens automatically.
 * Verify enriched prospects against search criteria
 * @param {Array} enrichedProspects - Enriched prospect data
 * @param {Object} icpData - ICP data for verification
 * @returns {Promise} Response with verified prospects
 */
export async function verifyProspects(enrichedProspects, icpData) {
  console.warn('verifyProspects is deprecated. Verification now happens automatically.');
  throw new Error('This endpoint is no longer available. Verification happens automatically.');
}

/**
 * @deprecated This endpoint is no longer available. Lists are created automatically.
 * Create a list with selected prospects
 * @param {Object} listData - List metadata (name, description)
 * @param {Array} prospects - Array of prospect objects to add
 * @returns {Promise} Response with created list
 */
export async function createAutoPilotList(listData, prospects) {
  console.warn('createAutoPilotList is deprecated. Lists are now created automatically.');
  throw new Error('This endpoint is no longer available. Lists are created automatically.');
}

/**
 * Generate searches based on ICP and clusters
 * @param {Object} data - Search generation data
 * @param {Object} data.icp - ICP object with who_is_this_person, what_will_they_do, etc.
 * @param {Object} data.clusters_for_sourcing_pro - Clusters object with titles, organisations, skills, etc.
 * @returns {Promise<Object>} Response with generated searches
 * @example
 * const response = await generateSearches({
 *   icp: { who_is_this_person: [...], ... },
 *   clusters_for_sourcing_pro: { titles: [...], organisations: [...] }
 * });
 */
export async function generateSearches({ icp, clusters_for_sourcing_pro }) {
  try {
    const response = await api.post('/icp/searches', {
      icp,
      clusters_for_sourcing_pro
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Search for persons based on clusters
 * @param {Object} data - Person search data
 * @param {Object} data.clusters - Clusters object with titles, organisations, skills, experience_year_patterns, locations
 * @param {Object} [data.meta] - Meta object with count
 * @param {number} [data.size_per_query=10] - Size per query
 * @param {boolean} [data.titlecase=false] - Whether to use titlecase
 * @param {string} [data.dataset='all'] - Dataset to use
 * @param {boolean} [data.use_sandbox=true] - Whether to use sandbox
 * @param {number} [data.concurrency=3] - Concurrency level
 * @returns {Promise<Object>} Response with person search results
 * @example
 * const response = await personSearch({
 *   clusters: { titles: [...], organisations: [...], skills: [...], locations: [...] },
 *   meta: { count: 3 },
 *   size_per_query: 10,
 *   titlecase: false,
 *   dataset: 'all',
 *   use_sandbox: true,
 *   concurrency: 3
 * });
 */
export async function personSearch({
  clusters,
  meta = { count: 3 },
  size_per_query = 10,
  titlecase = false,
  dataset = 'all',
  use_sandbox = true,
  concurrency = 3
}) {
  try {
    const response = await api.post('/icp/person-search', {
      clusters,
      meta,
      size_per_query,
      titlecase,
      dataset,
      use_sandbox,
      concurrency
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

/**
 * Setup Slack integration for list notifications
 * @param {string} listId - List ID
 * @param {string} slackWebhook - Slack webhook URL
 * @returns {Promise} Response with integration status
 */
export async function setupSlackIntegration(listId, slackWebhook) {
  try {
    const response = await api.post('/autopilot/slack-integration', {
      listId,
      slackWebhook
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}
